/**
 * API module for sending conversion events to the server
 */

import { enqueueEvent } from './queue.js';
import { log, warn, error as logError } from './logger.js';

/**
 * Send a conversion event to the API
 * Implements retry logic: retry once on failure, then queue for background retry
 *
 * @param {string} endpoint - The API endpoint URL
 * @param {Object} payload - The event payload
 * @param {string} payload.clickId - The click ID (camelCase)
 * @param {string} payload.event - The event name
 * @param {number|null} payload.value - Monetary value (optional)
 * @param {string|null} payload.currency - Currency code (optional)
 * @param {Object} payload.properties - Event properties
 * @param {string} payload.timestamp - ISO 8601 timestamp
 * @param {string} [payload.signature] - HMAC signature (optional, currently disabled)
 * @param {string} apiKey - The API key for authentication
 * @param {boolean} [skipQueue=false] - If true, don't queue on failure (used by queue processor to avoid infinite loops)
 * @returns {Promise<Object>} Response from the API
 */
async function sendEvent(endpoint, payload, apiKey, skipQueue = false) {
  const maxRetries = 1;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': apiKey
        },
        body: JSON.stringify(payload)
      });

      // Parse response
      const data = await response.json();

      // Check if request was successful
      if (response.ok) {
        log('[Rebrandly SDK] Event tracked successfully:', data);
        return {
          success: true,
          data: data
        };
      }

      // Handle 425 click_not_yet_available — queue for retry
      if (response.status === 425 && data.error === 'click_not_yet_available') {
        warn('[Rebrandly SDK] Click not yet available, queueing for retry...');

        if (!skipQueue) {
          const queueResult = enqueueEvent(endpoint, payload, apiKey, {
            reason: 'click_not_yet_available',
            maxRetries: 24 // ~2 minutes at 5s interval
          });

          if (queueResult.success) {
            return {
              success: false,
              error: 'click_not_yet_available',
              message: data.message || 'Click not yet available',
              queued: true,
              queueId: queueResult.queueId,
              statusCode: 425
            };
          }
        }

        // skipQueue=true (from queue processor) or enqueue failed
        return {
          success: false,
          error: 'click_not_yet_available',
          message: data.message || 'Click not yet available',
          queued: false,
          statusCode: 425
        };
      }

      // Server returned an error
      warn(
        `[Rebrandly SDK] Server error (${response.status}):`,
        data.error || data.message || 'Unknown error'
      );

      return {
        success: false,
        error: data.error || 'server_error',
        message: data.message || `HTTP ${response.status}`,
        statusCode: response.status
      };

    } catch (error) {
      lastError = error;

      // If this is not the last attempt, retry
      if (attempt < maxRetries) {
        warn(
          `[Rebrandly SDK] Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`,
          error.message
        );

        // Wait a bit before retrying (100ms)
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }

      // Final attempt failed
      logError('[Rebrandly SDK] Request failed after retries:', error.message);
    }
  }

  // All attempts failed - queue for background retry unless skipQueue is true
  if (!skipQueue) {
    log('[Rebrandly SDK] Queueing event for background retry...');
    const queueResult = enqueueEvent(endpoint, payload, apiKey);

    if (queueResult.success) {
      return {
        success: false,
        error: 'network_error',
        message: lastError?.message || 'Failed to send event after retries',
        queued: true,
        queueId: queueResult.queueId
      };
    }
  }

  // Failed and couldn't queue (or skipQueue was true)
  return {
    success: false,
    error: 'network_error',
    message: lastError?.message || 'Failed to send event after retries',
    queued: false
  };
}

/**
 * Send a domain verification ping to the API
 *
 * @param {string} endpoint - The ping endpoint URL (e.g., https://track.rebrandly.com/v1/sdk/ping)
 * @param {string} token - The verification token (e.g., vrf_abc123)
 * @param {string} domain - The domain being verified (e.g., customer.com)
 * @param {string} apiKey - The API key for authentication
 * @returns {Promise<Object>} Result of the ping request
 */
async function sendPing(endpoint, token, domain, apiKey) {
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': apiKey
      },
      body: JSON.stringify({ token, domain })
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    }

    return {
      success: false,
      error: data.error || 'server_error',
      message: data.message || `HTTP ${response.status}`
    };
  } catch (error) {
    return {
      success: false,
      error: 'network_error',
      message: error.message
    };
  }
}

/**
 * Validate that a base URL is properly formatted
 * @param {string} url - The base URL (e.g., 'https://track.rebrandly.com')
 * @returns {boolean}
 */
function isValidBaseUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
}

export {
  sendEvent,
  sendPing,
  isValidBaseUrl
};
